import { Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import MainLayout from '../../../layouts/MainLayout';
import { Loader, Center } from '@/components/ui';

// Eager load dashboard for instant access
import ProcurementsDashboard from './dashboard';

// Lazy load all other pages for better performance
// Requests
const MyRequestsPage = lazy(() => import('./requests/my-requests'));
const AllRequestsPage = lazy(() => import('./requests/all-requests'));
const CreateRequestPage = lazy(() => import('./requests/create-request'));
const RequestDetailPage = lazy(() => import('./requests/view/[id]'));

// Approvals
const PendingApprovalsPage = lazy(() => import('./approvals/pending'));
const ApprovalHistoryPage = lazy(() => import('./approvals/history'));

// Suppliers
const SupplierListPage = lazy(() => import('./suppliers/list'));
const SupplierDetailPage = lazy(() => import('./suppliers/view/[id]'));

// Subscriptions
const SubscriptionListPage = lazy(() => import('./subscriptions/list'));
const SubscriptionDetailPage = lazy(() => import('./subscriptions/view/[id]'));
const CreateSubscriptionPage = lazy(() => import('./subscriptions/create'));

// Purchase Orders & Invoices
const PurchaseOrdersPage = lazy(() => import('./purchase-orders'));
const InvoicesPage = lazy(() => import('./invoices'));

// Finance
const FinancePortalPage = lazy(() => import('./finance'));

// Catalogue
const ItemsPage = lazy(() => import('./items'));
const ItemDetailPage = lazy(() => import('./items/[id]'));

// Admin
const AdminPage = lazy(() => import('./admin'));
const AdminLookupsPage = lazy(() => import('./admin/lookups'));
const LookupCategoryPage = lazy(() => import('./admin/lookups/[category]'));
const LookupManagementPage = lazy(() => import('./admin/lookups/[category]/[lookup]'));
const RoleAssignmentsPage = lazy(() => import('./admin/roles'));
const SettingsPage = lazy(() => import('./admin/settings'));

// Loading fallback
const LoadingFallback = () => (
  <Center h={400}>
    <Loader size="lg" />
  </Center>
);

/**
 * Procurement Management Routes
 * Routing configuration for Procurements domain with MainLayout
 */
export default function ProcurementManagementRoutes() {
  return (
    <MainLayout domainKey="procurements">
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* Dashboard - Default route */}
          <Route path="/" element={<ProcurementsDashboard />} />

          {/* Requests */}
          <Route path="/requests/my-requests" element={<MyRequestsPage />} />
          <Route path="/requests/all-requests" element={<AllRequestsPage />} />
          <Route path="/requests/create-request" element={<CreateRequestPage />} />
          <Route path="/requests/view/:id" element={<RequestDetailPage />} />

          {/* Approvals */}
          <Route path="/approvals/pending" element={<PendingApprovalsPage />} />
          <Route path="/approvals/history" element={<ApprovalHistoryPage />} />

          {/* Suppliers */}
          <Route path="/suppliers/list" element={<SupplierListPage />} />
          <Route path="/suppliers/view/:id" element={<SupplierDetailPage />} />

          {/* Subscriptions */}
          <Route path="/subscriptions" element={<SubscriptionListPage />} />
          <Route path="/subscriptions/view/:id" element={<SubscriptionDetailPage />} />
          <Route path="/subscriptions/create" element={<CreateSubscriptionPage />} />

          {/* Purchase Orders & Invoices */}
          <Route path="/purchase-orders" element={<PurchaseOrdersPage />} />
          <Route path="/invoices" element={<InvoicesPage />} />

          {/* Finance */}
          <Route path="/finance" element={<FinancePortalPage />} />

          {/* Catalogue */}
          <Route path="/items" element={<ItemsPage />} />
          <Route path="/items/:id" element={<ItemDetailPage />} />

          {/* Admin */}
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/admin/lookups" element={<AdminLookupsPage />} />
          <Route path="/admin/lookups/:category" element={<LookupCategoryPage />} />
          <Route path="/admin/lookups/:category/:lookup" element={<LookupManagementPage />} />
          <Route path="/admin/roles" element={<RoleAssignmentsPage />} />
          <Route path="/admin/settings" element={<SettingsPage />} />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/app/procurements" replace />} />
        </Routes>
      </Suspense>
    </MainLayout>
  );
}
